import { IconButton } from '@mui/material';
import { memo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import {
  clearFeedback,
  selectSubmittedFeedback,
} from '../../state/aiSidekickChatSlice';
import type { StateWithSidekickChat } from '../../state/chatTypes';
import { SIDEKICK_TRANSLATION_NAMESPACE } from '../../translations';
import OverflowDotsIcon from '../icons/OverflowDotsIcon';
import ThumbsDownIcon from '../icons/ThumbsDownIcon';
import ThumbsUpIcon from '../icons/ThumbsUpIcon';
import MessageFeedbackInlineForm from '../MessageFeedbackInlineForm';
import useMessageActionButtonStyles from '../messageActionButton.styles';
import { showSidekickErrorToast, showSidekickSuccessToast } from '../toast';
import { OVERFLOW_ICON_SIZE_PX, THUMBS_ICON_SIZE_PX } from './constants';
import useStyles from './MessageFeedbackRow.styles';
import type { MessageFeedbackRowProps } from './types';
import useMessageFeedbackSubmission from './useMessageFeedbackSubmission';

/**
 * Per-message feedback controls rendered directly below every
 * assistant `MessageBubble`. Exposes up to three controls, optionally
 * preceded by whatever the caller passes as `leadingActions`:
 *
 * - Thumbs-up — toggles a `'up'` rating. Clicking the active control
 *   clears the rating (returns to neutral). Mutually exclusive with
 *   thumbs-down.
 * - Thumbs-down — mirror of thumbs-up.
 * - Overflow (`...`) — renders **only after** the user has recorded
 *   a rating, matching the GA-Sidekick Figma source (neutral state
 *   shows only the two thumbs; the overflow appears alongside them
 *   once a rating exists). Clicking the overflow toggles the inline
 *   freeform feedback card open and shifts the overflow into its
 *   filled (`brand[100]`) state. Re-clicking the overflow — or the
 *   close glyph inside the form — collapses the card and discards
 *   any unsent draft, matching the Figma annotations on frame
 *   `16166:25718`.
 *
 * Selection (and any submitted comment) is stored locally on the
 * chat slice's `submittedFeedback` map keyed by `messageId`. The
 * rating persists across re-renders and scroll. When the host supplies
 * `submitFeedback`, inline-form submission persists through that
 * callback before local comment state is updated. Success and error
 * toasts use the design-system `sonner` Toaster mounted by the host app.
 */
function MessageFeedbackRowInner({
  messageId,
  conversationId,
  traceId,
  leadingActions,
  className,
}: MessageFeedbackRowProps) {
  const { classes, cx } = useStyles();
  const { classes: buttonClasses } = useMessageActionButtonStyles();
  const { t } = useTranslation(SIDEKICK_TRANSLATION_NAMESPACE);
  const dispatch = useDispatch();
  const { selectRating, submitComment } = useMessageFeedbackSubmission({
    messageId,
    conversationId,
    traceId,
  });

  const rating = useSelector(
    (state: StateWithSidekickChat) =>
      selectSubmittedFeedback(state)[messageId]?.rating
  );

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isRatingPending, setIsRatingPending] = useState(false);

  const isUp = rating === 'up';
  const isDown = rating === 'down';
  const hasRating = isUp || isDown;

  const showFeedbackError = useCallback(() => {
    showSidekickErrorToast({
      message: t('chat.feedback.errorToast'),
      closeAriaLabel: t('chat.feedback.errorToastDismissAriaLabel'),
      testId: 'ai-sidekick-feedback-error-toast',
    });
  }, [t]);

  const handleRatingError = useCallback(() => {
    // The rating was cleared back to neutral by the submission hook;
    // collapse the form too so it cannot silently reopen on the next
    // rating, then surface the generic error toast.
    setIsFormOpen(false);
    showFeedbackError();
  }, [showFeedbackError]);

  const submitRating = useCallback(
    (next: 'up' | 'down') => {
      setIsRatingPending(true);
      void selectRating(next)
        .catch(handleRatingError)
        .finally(() => {
          setIsRatingPending(false);
        });
    },
    [selectRating, handleRatingError]
  );

  const handleThumbsUp = useCallback(() => {
    // Ignore clicks while a rating submission is in flight: a rapid
    // second click would otherwise race the host request (e.g. switching
    // up→down spawns a competing write, or toggling to neutral hides a
    // rating the backend still persists), leaving the UI out of sync with
    // what was saved.
    if (isRatingPending) {
      return;
    }
    if (rating === 'up') {
      dispatch(clearFeedback({ messageId }));
      // Clearing the rating also collapses the inline form so the
      // overflow can disappear cleanly from the layout. The backend
      // has no retract, so toggling to neutral stays local-only.
      setIsFormOpen(false);
      return;
    }
    submitRating('up');
  }, [dispatch, messageId, rating, isRatingPending, submitRating]);

  const handleThumbsDown = useCallback(() => {
    if (isRatingPending) {
      return;
    }
    if (rating === 'down') {
      dispatch(clearFeedback({ messageId }));
      setIsFormOpen(false);
      return;
    }
    submitRating('down');
  }, [dispatch, messageId, rating, isRatingPending, submitRating]);

  const handleOverflow = useCallback(() => {
    setIsFormOpen((open) => !open);
  }, []);

  const handleFormClose = useCallback(() => {
    setIsFormOpen(false);
  }, []);

  const handleFormSubmit = useCallback(
    async (_id: string, comment: string) => {
      // Runtime guard so TS narrows `rating` to `'up' | 'down'`. The
      // return is unreachable in practice — the inline form only
      // mounts when `hasRating` is true — but it keeps the payload
      // typed without an `as` assertion.
      if (rating !== 'up' && rating !== 'down') return;
      await submitComment(rating, comment);
    },
    [rating, submitComment]
  );

  const handleFormSubmitSuccess = useCallback(() => {
    showSidekickSuccessToast({
      message: t('chat.feedback.submittedToast'),
      closeAriaLabel: t('chat.feedback.submittedToastDismissAriaLabel'),
      testId: 'ai-sidekick-feedback-submitted-toast',
    });
  }, [t]);

  return (
    <div
      className={classes.wrapper}
      data-testid={`ai-sidekick-feedback-${messageId}`}
    >
      <div className={cx(classes.row, className)}>
        {leadingActions}
        <IconButton
          className={cx(
            buttonClasses.button,
            isUp && buttonClasses.buttonActive
          )}
          onClick={handleThumbsUp}
          aria-label={t('chat.feedback.thumbsUpAriaLabel')}
          aria-pressed={isUp}
          data-testid="ai-sidekick-feedback-thumbs-up"
        >
          <ThumbsUpIcon size={THUMBS_ICON_SIZE_PX} />
        </IconButton>
        <IconButton
          className={cx(
            buttonClasses.button,
            isDown && buttonClasses.buttonActive
          )}
          onClick={handleThumbsDown}
          aria-label={t('chat.feedback.thumbsDownAriaLabel')}
          aria-pressed={isDown}
          data-testid="ai-sidekick-feedback-thumbs-down"
        >
          <ThumbsDownIcon size={THUMBS_ICON_SIZE_PX} />
        </IconButton>
        {hasRating && (
          <IconButton
            className={cx(
              buttonClasses.button,
              isFormOpen && buttonClasses.buttonActive
            )}
            onClick={handleOverflow}
            aria-label={t('chat.feedback.overflowAriaLabel')}
            aria-expanded={isFormOpen}
            aria-controls={
              isFormOpen ? `ai-sidekick-feedback-form-${messageId}` : undefined
            }
            data-testid="ai-sidekick-feedback-overflow"
          >
            <OverflowDotsIcon size={OVERFLOW_ICON_SIZE_PX} />
          </IconButton>
        )}
      </div>
      {hasRating && isFormOpen && (
        <MessageFeedbackInlineForm
          messageId={messageId}
          onSubmit={handleFormSubmit}
          onClose={handleFormClose}
          onSubmitSuccess={handleFormSubmitSuccess}
          onSubmitError={showFeedbackError}
        />
      )}
    </div>
  );
}

const MessageFeedbackRow = memo(MessageFeedbackRowInner);
MessageFeedbackRow.displayName = 'MessageFeedbackRow';

export default MessageFeedbackRow;
